import React, { useRef, useState } from 'react';
import { Upload, FolderOpen, Users, X, Film } from 'lucide-react';
import { uploadFileToBackend } from '../services/backendService';
import { UserAssetsModal } from './wizard/UserAssetsModal';
import { ActorsLibraryModal } from './wizard/ActorsLibraryModal';

export const MAX_REFERENCE_VIDEOS = 10;
export const MAX_REFERENCE_VIDEO_BYTES = 50 * 1024 * 1024;

export const validateReferenceVideo = (file, currentCount) => {
    if (!file) return 'No file selected.';
    if (currentCount >= MAX_REFERENCE_VIDEOS) return `You can add up to ${MAX_REFERENCE_VIDEOS} reference videos.`;
    if (!(file.type || '').startsWith('video/')) return `"${file.name}" is not a video file.`;
    if (file.size > MAX_REFERENCE_VIDEO_BYTES) return `"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — the limit is 50 MB.`;
    return null;
};

// Request payload shape: reference_videos[]
export const buildReferenceVideosPayload = (videos) => (Array.isArray(videos) ? videos : [])
    .filter(v => v && typeof v.url === 'string' && v.url)
    .slice(0, MAX_REFERENCE_VIDEOS)
    .map(v => ({
        url: v.url,
        instruction: (v.instruction || '').trim(),
        file_name: v.fileName || null,
        mime_type: v.mimeType || null,
        duration_seconds: Number.isFinite(v.durationSeconds) ? Math.round(v.durationSeconds * 100) / 100 : null,
    }));

const probeDuration = (src) => new Promise((resolve) => {
    const v = document.createElement('video');
    const done = (d) => { clearTimeout(t); v.removeAttribute('src'); resolve(d); };
    const t = setTimeout(() => done(null), 8000);
    v.preload = 'metadata';
    v.onloadedmetadata = () => done(Number.isFinite(v.duration) ? v.duration : null);
    v.onerror = () => done(null);
    v.src = src;
});

const btn = 'px-2.5 py-1.5 text-xs rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 flex items-center gap-1.5 disabled:opacity-40 transition-colors';

export const ReferenceVideosPanel = ({ videos, setVideos, disabled }) => {
    const fileRef = useRef(null);
    const [picker, setPicker] = useState(null);
    const [uploading, setUploading] = useState(0);
    const [error, setError] = useState(null);
    const full = videos.length + uploading >= MAX_REFERENCE_VIDEOS;

    const add = (v) => setVideos(prev => (prev.length >= MAX_REFERENCE_VIDEOS || prev.some(p => p.url === v.url) ? prev : [...prev, v]));

    const handleFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        setError(null);
        let count = videos.length;
        const valid = [];
        for (const f of files) {
            const err = validateReferenceVideo(f, count);
            if (err) { setError(err); continue; }
            valid.push(f); count++;
        }
        setUploading(n => n + valid.length);
        await Promise.all(valid.map(async (f) => {
            try {
                const local = URL.createObjectURL(f);
                const durationSeconds = await probeDuration(local);
                URL.revokeObjectURL(local);
                const url = await uploadFileToBackend(f);
                add({ id: `rv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, url, fileName: f.name, mimeType: f.type, durationSeconds, instruction: '', source: 'upload' });
            } catch (ex) {
                setError(`Upload failed for "${f.name}": ${ex.message}`);
            } finally {
                setUploading(n => n - 1);
            }
        }));
    };

    const addFromUrl = async (url, name, source) => {
        setPicker(null);
        if (!url) return;
        const durationSeconds = await probeDuration(url);
        add({ id: `rv-${Date.now()}`, url, fileName: name || null, mimeType: 'video/mp4', durationSeconds, instruction: '', source });
    };

    const update = (id, instruction) => setVideos(prev => prev.map(v => (v.id === id ? { ...v, instruction } : v)));

    return (
        <section className="w-full mt-4 p-3 rounded-xl bg-[#101018] border border-gray-700" data-testid="reference-videos-panel">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div>
                    <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Reference videos <span className="text-zinc-500 font-normal normal-case">({videos.length}/{MAX_REFERENCE_VIDEOS}, this generation only)</span></h4>
                    <p className="text-[11px] text-zinc-500">Add a note for each video saying how it should be used (e.g. "copy the camera move", "match this dance").</p>
                </div>
                <div className="flex gap-1.5">
                    <input ref={fileRef} type="file" accept="video/*" multiple className="hidden" onChange={handleFiles} data-testid="ref-videos-file-input" />
                    <button className={btn} disabled={disabled || full} onClick={() => fileRef.current?.click()} data-testid="ref-videos-upload">
                        {uploading > 0 ? <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        {uploading > 0 ? `Uploading ${uploading}…` : 'Upload'}
                    </button>
                    <button className={btn} disabled={disabled || full} onClick={() => setPicker('assets')} data-testid="ref-videos-user-assets"><FolderOpen className="w-3.5 h-3.5" /> User assets</button>
                    <button className={btn} disabled={disabled || full} onClick={() => setPicker('library')} data-testid="ref-videos-library"><Users className="w-3.5 h-3.5" /> Library</button>
                </div>
            </div>
            {error && <p className="text-[11px] text-red-400 mb-2" data-testid="ref-videos-error">{error}</p>}
            {videos.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    {videos.map((v, i) => (
                        <div key={v.id} className="flex gap-2 p-2 rounded-lg bg-black/30 border border-gray-700" data-testid={`ref-video-${i}`}>
                            <div className="w-28 shrink-0">
                                <video src={v.url} controls muted className="w-full aspect-video rounded bg-black" data-testid={`ref-video-preview-${i}`} />
                                <p className="text-[10px] text-zinc-500 truncate mt-0.5 flex items-center gap-1"><Film className="w-3 h-3" />{v.durationSeconds ? `${v.durationSeconds.toFixed(1)}s` : '—'} · {v.fileName || 'video'}</p>
                            </div>
                            <textarea value={v.instruction} onChange={e => update(v.id, e.target.value)} disabled={disabled} rows={3}
                                placeholder={`How should video ${i + 1} be used?`}
                                className="flex-1 min-w-0 p-2 bg-gray-800 border border-gray-700 rounded-md text-xs text-gray-200 resize-y focus:border-purple-500 focus:outline-none"
                                data-testid={`ref-video-instruction-${i}`} />
                            <button onClick={() => setVideos(prev => prev.filter(x => x.id !== v.id))} disabled={disabled}
                                className="self-start p-1 rounded hover:bg-red-500/20 text-red-400" title="Remove" data-testid={`ref-video-remove-${i}`}><X className="w-4 h-4" /></button>
                        </div>
                    ))}
                </div>
            )}
            {picker === 'assets' && (
                <UserAssetsModal activeTab="media" onClose={() => setPicker(null)}
                    onSelect={(url, asset) => addFromUrl(url, asset?.name, 'user-assets')} />
            )}
            {picker === 'library' && (
                <ActorsLibraryModal onClose={() => setPicker(null)}
                    onSelect={(a) => {
                        if (!a?.previewVideoUrl) { setPicker(null); setError(`"${a?.name || 'This actor'}" has no preview video.`); return; }
                        addFromUrl(a.previewVideoUrl, a.name ? `${a.name} preview` : null, 'library');
                    }} />
            )}
        </section>
    );
};
